import { AllValueType, MayString, formatTokenAmount, isAll } from "@bridgesplit/utils";
import { AssetData, TokenListMetadata, TokenListMetadataExpanded, TokenListTag } from "@bridgesplit/abf-sdk";

import { AssetTypeIdentifier, LoanCollateral, TokenBalance } from "../types";
import { isRaydiumMetadata, isWhirlpoolMetadata } from "../api";
import { shortFormatTokenSymbol } from "./ui";

const PLACEHOLDER_IMAGE = "https://bridgesplit-app.s3.amazonaws.com/tokens/placeholder.png";

type BsMetaUtilInput = TokenListMetadataExpanded | undefined | null;

export class BsMetaUtil {
    static getName(metadata: BsMetaUtilInput): string {
        return this.getNameNoFallback(metadata) || "Unknown Asset";
    }

    static getNameNoFallback(metadata: BsMetaUtilInput): string | undefined {
        let name = metadata?.name?.replace(/\0/g, "");

        if (name) {
            return name;
        }

        name = metadata?.name?.replace(/\0/g, "");
        if (name) {
            return name;
        }

        return undefined;
    }

    static getImage(metadata: BsMetaUtilInput, options?: { hideCdn?: boolean; size?: number }): string {
        if (!metadata) return "";
        let image = metadata.imageUrl;

        if (!image) {
            return PLACEHOLDER_IMAGE;
        }
        image = parseImageUri(image);
        if (options?.hideCdn) return image;
        const defaultSize = this.isFungible(metadata) ? 60 : 400;
        return getCdnUrl(image, options?.size ?? defaultSize);
    }

    static getDescription(metadata: BsMetaUtilInput) {
        return "No description provided";
    }

    static getSymbol(metadata: BsMetaUtilInput | AllValueType, options?: { shortSymbol?: boolean }) {
        if (!metadata || isAll(metadata)) return "";
        if (!metadata.symbol) return "UNKNOWN";

        if (options?.shortSymbol) {
            return shortFormatTokenSymbol(metadata) ?? metadata.symbol;
        }
        return metadata.symbol;
    }

    static getNameOrSymbol(metadata: BsMetaUtilInput) {
        if (this.isNonFungible(metadata)) {
            return this.getName(metadata);
        }
        return this.getSymbol(metadata);
    }

    static isFungible(metadata: BsMetaUtilInput) {
        if (!metadata) return false;
        return !this.isNonFungible(metadata);
    }

    static isNonFungible(metadata: BsMetaUtilInput) {
        if (!metadata) return false;
        return metadata.decimals === 0 && metadata.assetType !== AssetTypeIdentifier.SplToken;
    }

    static formatAmount(
        metadata: BsMetaUtilInput,
        amount: number | undefined,
        options?: {
            hideSymbol?: boolean;
            decimals?: number;
            shortSymbol?: boolean;
            compact?: boolean;
            signed?: boolean;
        }
    ) {
        const isLp = metadata?.tags?.includes(TokenListTag.LP);
        if (this.isNonFungible(metadata)) {
            if (isLp) {
                return this.getSymbol(metadata, { shortSymbol: options?.shortSymbol });
            }
            return this.getSymbolUnique(metadata, { shortSymbol: options?.shortSymbol });
        }

        return formatTokenAmount(amount, {
            symbol: isLp
                ? this.getSymbol(metadata, { shortSymbol: options?.shortSymbol })
                : BsMetaUtil.getSymbolUnique(metadata, { shortSymbol: options?.shortSymbol }),
            decimals: options?.decimals ?? metadata?.decimals,
            hideSymbol: options?.hideSymbol,
            compactLargeNumbers: options?.compact,
            signed: options?.signed
        });
    }

    static formatRawAmount(
        metadata: BsMetaUtilInput,
        amount: number | undefined,
        options?: { hideSymbol?: boolean; decimals?: number; shortSymbol?: boolean; compact?: boolean }
    ) {
        const isLp = metadata?.tags?.includes(TokenListTag.LP);
        if (this.isNonFungible(metadata)) {
            if (isLp) {
                return this.getSymbol(metadata, { shortSymbol: options?.shortSymbol });
            }
            return this.getSymbolUnique(metadata, { shortSymbol: options?.shortSymbol });
        }

        return formatTokenAmount(amount, {
            symbol: isLp
                ? this.getSymbol(metadata, { shortSymbol: options?.shortSymbol })
                : BsMetaUtil.getSymbolUnique(metadata, { shortSymbol: options?.shortSymbol }),
            decimals: options?.decimals ?? metadata?.decimals,
            hideSymbol: options?.hideSymbol,
            isRawAmount: true,
            compactLargeNumbers: options?.compact
        });
    }

    static isValid(input: object | undefined | null): input is TokenListMetadata {
        if (!input) return false;
        if ("mint" in input && "assetType" in input) return true;
        return false;
    }

    // Returns unique name for NFTs, LPs, and other assets with the same token symbol
    static getSymbolUnique(metadata: BsMetaUtilInput | AllValueType, options?: { shortSymbol?: boolean }): string {
        if (isAll(metadata)) return "All";
        if (this.isNonFungible(metadata) || metadata?.tags?.includes(TokenListTag.LP)) {
            return this.getName(metadata);
        }
        return this.getSymbol(metadata, { shortSymbol: options?.shortSymbol });
    }
}

export function getCdnUrl(image: MayString, size: number) {
    if (!image) return "";
    return image;
}

export function parseImageUri(image: string) {
    return image.replace("ipfs://", "https://ipfs.io/ipfs/");
}

export function getSearchFromMetadata(metadata: BsMetaUtilInput) {
    return [BsMetaUtil.getName(metadata), BsMetaUtil.getSymbol(metadata)];
}

export function formatTokens(
    tokens: Omit<TokenBalance, "mint">[] | undefined,
    options?: { hideSymbol?: boolean; condensed?: boolean; showAll?: boolean }
) {
    if (tokens?.length === 1) {
        const token = tokens[0];
        if (BsMetaUtil.isNonFungible(token.metadata) && options?.condensed) {
            return BsMetaUtil.getSymbol(token.metadata);
        }
        return BsMetaUtil.formatAmount(token.metadata, token.amount, { hideSymbol: options?.hideSymbol });
    }

    if (options?.showAll) {
        return tokens
            ?.map((t) => BsMetaUtil.formatAmount(t.metadata, t.amount, { hideSymbol: options?.hideSymbol }))
            .join(" + ");
    }

    return `${tokens?.length || 0} assets`;
}

export function formatTokenMetadata(tokens: TokenListMetadata[] | undefined, term = "assets") {
    return tokens?.length === 1 ? BsMetaUtil.getSymbol(tokens[0]) : `${tokens?.length || 0} ${term}`;
}
export function getAssetType(metadata: BsMetaUtilInput): AssetTypeIdentifier {
    if (!metadata) return 0;
    return metadata.assetType;
}

export function getAssetIdentifier(metadata: BsMetaUtilInput): string | undefined {
    if (!metadata) return undefined;
    // If it's a whirlpool/raydium metadata the assetIdentifier should be the whirlpoolAddress or the poolAddress

    if (isWhirlpoolMetadata(metadata)) {
        return metadata?.whirlpoolOffchain?.address;
    }
    if (isRaydiumMetadata(metadata)) {
        return metadata?.raydiumOffchain?.id;
    }
    return metadata.mint;
}

export function getAssetMint(metadata: BsMetaUtilInput): string | undefined {
    // If it's a whirlpool metadata, the assetMint should be the NFT mint or the positionMint
    return metadata?.mint;
}
export function isCollateralSplMint(asset: LoanCollateral | undefined): boolean {
    return asset?.assetType === AssetTypeIdentifier.SplToken;
}

export function getAssetData(
    metadata: TokenListMetadata | undefined,
    clmmMint: string | undefined
): AssetData | undefined {
    if (!metadata || !clmmMint) return undefined;

    switch (getAssetType(metadata)) {
        case AssetTypeIdentifier.SplToken: {
            return {
                Spl: {
                    mint: metadata.mint
                }
            };
        }
        case AssetTypeIdentifier.StakedSol: {
            return {
                StakedSol: {
                    stakeAccount: metadata.mint,
                    stakePool: metadata.mint
                }
            };
        }
        case AssetTypeIdentifier.OrcaPosition: {
            return {
                Orca: {
                    positionMint: clmmMint,
                    whirlpool: metadata.mint
                }
            };
        }
        case AssetTypeIdentifier.RaydiumPosition: {
            return {
                Raydium: {
                    mint: clmmMint,
                    pool: metadata.mint
                }
            };
        }
    }
}

export function getMintFromAssetData(data: AssetData) {
    if ("Spl" in data) {
        return data.Spl.mint;
    }
    if ("StakedSol" in data) {
        return data.StakedSol.stakeAccount;
    }
    if ("Orca" in data) {
        return data.Orca.positionMint;
    }
    if ("Raydium" in data) {
        return data.Raydium.mint;
    }
    throw new Error("Unknown asset type");
}
